import React, { useEffect, useRef, useState } from 'react';
import { useContext } from 'react';
import ProductBanner from '@/globalComponents/productBanner';
import { ProductContext } from '@/context/productContext';
import Modal from '@/globalComponents/modal';
import { fetchAmcPlans } from '@/actions/fetchAmcPlans';
import { useRouter } from 'next/router';
import { WebEngageContext } from '@/context/webengageContext';
import { ServiceContext } from '@/context/serviceContext';
import { tabItemNames } from '../Service/TabContainer/tabItems';
import styles from './style.module.scss';
import { serviceRequestHistory } from '@/actions/fetchServiceHistory';
import { ServiceRequestContext } from '@/context/serviceRequestContext';
import { transformServiceHistory } from '@/utils/serviceRequest';
import { PageLevelLoader } from '@/globalComponents/pageLevelLoader';
import { resetCart } from '@/actions/resetCart';
import { useFeatureToggle } from '@/hooks/useFeatureToggle';
import { featureFlags } from '@/configs/featureFlags';
import Div from '@/shared/Div';
import Button from '@/shared/Button';
import Paragraph from '@/shared/Paragraph';
import CrossIcon from '../Icons/Cross';
import useGTM from '@/hooks/useGTM';
import { GTMEventNames } from '@/types/gtm';

/**
 * This container will act as a wrapper for change device logic
 * @param props
 * @returns
 */

export function disableScroll() {
  var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  var scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

  document.body.style.overflow = 'hidden';

  window.onscroll = function () {
    window.scrollTo(scrollLeft, scrollTop);
  };
}

export function enableScroll() {
  document.body.style.overflow = 'auto';
  window.onscroll = null;
}

type ParamType = { assetid: any; showMyDevices?: boolean };

export const DeviceSelectionModal = (props: any) => {
  const { products, open, onClose, isServicePage, showConfirmButton, isRescheduleJourney } = props;
  const { dispatch: productAction, state: productState } = useContext(ProductContext);
  const [tempSelectedProduct, setTempSelectedProduct] = useState(productState.selectedProduct);
  const { state: serviceState } = useContext(ServiceContext);
  const { dispatch: serviceRequestAction } = useContext(ServiceRequestContext);
  const router = useRouter();
  const { state: webengageState } = useContext(WebEngageContext);
  const [isLoading, setIsLoading] = useState(false);
  const { fireGTMEvent } = useGTM();

  const selectedProductId = useRef(productState?.selectedProduct?.id);
  const [isFeatureEnabled] = useFeatureToggle();
  const resetCartEnabled = isFeatureEnabled(featureFlags.FEATURE_RESET_CART);

  useEffect(() => {
    if (selectedProductId.current !== productState.selectedProduct.id) {
      fireGTMEvent(GTMEventNames.SERVICE_SCREEN_DEVICE_CHANGED, {});
    }
  }, [productState.selectedProduct.id, fireGTMEvent]);

  const onDeviceSelection = async (device: any) => {
    onClose(false);
    productAction.setDisablePageScroll(false);
    disableScroll();
    let productId = productState.selectedProduct?.id;
    if (showConfirmButton) productId = tempSelectedProduct?.id;
    let params: ParamType = { assetid: device.id };

    if (productId ? device.id !== productId : true) {
      setIsLoading(true);

      productAction.setIsLoading(true);
      if (resetCartEnabled) {
        resetCart(productId);
      }
      if (showConfirmButton) productAction.setProductsData(tempSelectedProduct);
      else productAction.setProductsData(device);

      try {
        if (isServicePage) {
          const amcPlans = await fetchAmcPlans(device.id, device.deviceId);
          try {
            const serviceReqHistoryData = await serviceRequestHistory(device.id);
            serviceRequestAction.updateSRHistoryContext(
              transformServiceHistory(serviceReqHistoryData),
            );
          } catch (error) {
            serviceRequestAction.updateSRHistoryContext(null);
          }
          productAction.setAMCPlans(amcPlans);
        }
      } catch (error) {
      } finally {
        setIsLoading(false);
        productAction.setIsLoading(false);
        enableScroll();
      }
    }
    enableScroll();
    if (serviceState.activeTab === tabItemNames.MyDevices) {
      params.showMyDevices = true;
    }
    const queryStr: string = Object.entries(params)
      .map(([key, val]) => `${key}=${val}`)
      .join('&');
    router.replace(
      {
        query: params,
      },
      `${window.location.pathname}?${queryStr}`,
      {
        shallow: true,
      },
    );
  };

  const handleDeviceSelect = (product: any) => {
    setTempSelectedProduct(product);
  };

  const renderFooter = () => (
    <Div className="flex gap-s5 flex-col sticky p-4 z-10 bg-white shadow-subtle bottom-0 left-0 right-0">
      <Button
        dataTestId="request-type-next-button"
        theme="primary"
        className="w-full"
        isLoading={isLoading}
        onClick={() => {
          onDeviceSelection(productState?.selectedProduct);
        }}
      >
        Confirm
      </Button>
    </Div>
  );

  const renderHeader = () => (
    <Div className="flex items-center justify-between sticky  top-0 z-10 py-6  px-6  bg-white md:border-b md:border-b-neutral-50 shadow-subtle">
      <Paragraph className="text-h-3 md:text-hd-3"> Select a device</Paragraph>
      <Button
        theme="none"
        dataTestId="close-icon"
        onClick={() => {
          onClose(false);
          productAction.setDisablePageScroll(false);
        }}
      >
        <CrossIcon />
      </Button>
    </Div>
  );

  return (
    <>
      <Modal
        id="select-device"
        title="Select a device"
        titleStyle="text-h-3"
        showHeader={false}
        showCloseButton={false}
        onClose={() => {
          onClose(false);
          productAction.setDisablePageScroll(true);
        }}
        open={open}
        classNames="px-6 pt-6 overflow-scroll h-[600px] md:h-[302px] shadow-xbold"
        renderFooter={showConfirmButton && renderFooter}
        renderHeader={renderHeader}
      >
        <div
          className="flex flex-col md:gap-s6 gap-s5 md:max-h-screen md:w-[500px]"
          data-testid="product-container"
        >
          {products?.length
            ? products.map((product: any, index: number) => {
                const isSelected = showConfirmButton
                  ? tempSelectedProduct?.id === product.id
                  : productState.selectedProduct?.id === product.id;

                return (
                  <ProductBanner
                    key={`select-device-${product.id}`}
                    showSelection
                    deviceDetails={product}
                    isInsideModal
                    isSelected={isSelected}
                    onSelected={
                      showConfirmButton
                        ? () => {
                            if (!isSelected && isRescheduleJourney) {
                              router.back();
                              return;
                            }
                            handleDeviceSelect(product);
                          }
                        : onDeviceSelection
                    }
                    dataTestId={`select-device-${index}`}
                    badgeClasses="text-[0.5625rem] leading-[0.625rem] md:leading-[0.625rem] md:text-xs font-extrabold"
                    productImageClasses="h-[4.875rem] w-16 lg:h-[90px] lg:w-[95px]"
                    cardClasses={`py-3 pl-2 md:pl-5 md:pb-3 pr-8 hover:cursor-pointer md:max-h-[114px] md:max-w-[749px] ${styles.productBannerCardClass}`}
                    productTitleClasses={`font-bold text-[0.875rem] leading-[1.125rem] md:w-[70%] md:text-lg md:leading-7 ${styles.title}`}
                    productLocationClasses="text-xs font-normal md:text-base !leading-[.875rem] text-[#828282]"
                  ></ProductBanner>
                );
              })
            : null}
          <span className={styles.span}></span>
        </div>
      </Modal>

      {isLoading && <PageLevelLoader />}
    </>
  );
};

export default DeviceSelectionModal;
